import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useMemo, useCallback, useEffect, useRef} from 'react'
import type {IdToPullRequestMap} from '../types/pull'
import type {BaseTask, HydratedTask, OptimisticTask, UITask} from '../types/task'
import {fetchCAPI} from '../utils/fetch-capi'
import {mergeServerTasksWithOptimistic, transformAPITasksToUnhydrated} from '../utils/task-helpers'
import {useCAPIQuery} from './useCAPIQuery'
import {usePullRequests} from './usePullRequests'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDebounce} from '@github-ui/use-debounce'
import {tasksApiPathWithParams, type TasksQueryParams} from '../utils/api-url-helpers'
import type {APITask} from '../types/api-task'
import {useRepoInfo} from './use-repo-info'
import type {FlatTaskList} from './use-create-api-task'

export const agentTasksQueryKey = ['agent-tasks']
export const PAGE_SIZE = 10

export type TaskFilter = 'open' | 'closed' | 'all'

/**
 * Get the query key for agent tasks with pagination parameters.
 *
 * **Note:** pageSize is intentionally NOT included in the query key so that all views
 * (regardless of their pageSize setting) share the same cache. This allows optimistic
 * updates from task creation to appear in all views.
 * @param page - Page number
 * @param taskFilter - Which tasks to fetch: 'open', 'closed', or 'all'
 *
 */
export function getAgentTasksQueryKey(page: number, taskFilter: TaskFilter): readonly unknown[] {
  return [...agentTasksQueryKey, page, taskFilter]
}

/**
 * Get the query key for agent tasks to be used for mutations to ensure optimistic tasks are always added to the correct view.
 */
export function getAgentTasksQueryKeyForMutation(): readonly unknown[] {
  return [...agentTasksQueryKey, 1, 'open']
}

export type TaskCounts = {
  open: number
  closed: number
}

/**
 * Response tuple from fetchTasks:
 * - tasks list
 * - has_next_page boolean
 * - task counts (open/closed)
 *
 * TODO: Update this to use an object with named properties for clarity.
 */
export type FetchTasksResponse = [BaseTask[], boolean, TaskCounts?]

/**
 * Extract the flat task list from the FetchTasksResponse tuple.
 * Used with useCreateAPITask for optimistic updates.
 */
export function getFlatTaskList(data: FetchTasksResponse | undefined): FlatTaskList | undefined {
  return data?.[0] as FlatTaskList | undefined
}

/**
 * Create a new FetchTasksResponse with an updated task list.
 * Used with useCreateAPITask for optimistic updates.
 */
export function setFlatTaskList(oldData: FetchTasksResponse | undefined, newList: FlatTaskList): FetchTasksResponse {
  return [newList, oldData?.[1] ?? false, oldData?.[2]]
}

export const fetchTasks = async ({
  queryClient,
  copilotApiUrl,
  queryParams,
  queryKey,
}: {
  queryClient: QueryClient
  copilotApiUrl?: string | undefined
  queryParams?: TasksQueryParams
  queryKey?: readonly unknown[]
}): Promise<FetchTasksResponse> => {
  const path = tasksApiPathWithParams(queryParams)

  const response = await fetchCAPI({
    path,
    copilotApiUrl,
  })
  const data = await response.json()

  const apiTasks = data.tasks as APITask[]
  const serverTasks = transformAPITasksToUnhydrated(apiTasks)

  // Get existing tasks from cache so that we can find and preserve optimistic tasks until next poll
  // Use the provided queryKey to read from the correct cache (includes page/archived params)
  const existingTasks = queryClient.getQueryData<FetchTasksResponse>(queryKey ?? agentTasksQueryKey)?.[0] || []

  // Merge server tasks with existing optimistic tasks
  const optimisticTasks = existingTasks.filter(task => task.isOptimistic === true) as OptimisticTask[]
  const mergedTasks = mergeServerTasksWithOptimistic(serverTasks, optimisticTasks)

  // Extract pagination and counts from response
  const hasNextPage = !!data.has_next_page
  let taskCounts: TaskCounts | undefined
  if (typeof data.total_active_count === 'number' && typeof data.total_archived_count === 'number') {
    taskCounts = {
      open: data.total_active_count,
      closed: data.total_archived_count,
    }
  }

  return [mergedTasks, hasNextPage, taskCounts]
}

/**
 * Convert TaskFilter to the API's archived parameter.
 * 'open' -> false, 'closed' -> true, 'all' -> undefined
 */
function taskFilterToArchived(taskFilter: TaskFilter): boolean | undefined {
  if (taskFilter === 'all') return undefined
  return taskFilter === 'closed'
}

// TODO: Combine this with the use-repo-tasks hook once we are fully on the task API to avoid duplication
export function useTasks({
  sessionsPollingInterval = 3000,
  initialTasks,
  initialPullRequestsMap,
  taskFilter = 'open',
  includeCounts,
  currentPage = 1,
  pageSize = PAGE_SIZE,
}: {
  sessionsPollingInterval?: number | false
  initialTasks?: HydratedTask[]
  initialPullRequestsMap?: IdToPullRequestMap
  taskFilter?: TaskFilter
  includeCounts?: boolean
  currentPage?: number
  pageSize?: number
} = {}) {
  const queryClient = useQueryClient()
  const isAliveEnabled = isFeatureEnabled('copilot_agent_sessions_alive_updates')

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: agentTasksQueryKey,
    })
    queryClient.invalidateQueries({
      queryKey: ['agent-sessions-pull-requests'],
    })
  }, [queryClient])

  const debouncedHandleAliveUpdate = useDebounce(
    handleAliveUpdate,
    sessionsPollingInterval ? sessionsPollingInterval : 3000,
    {maxWait: 10000},
  )

  // Subscribe to alive updates if feature flag is enabled
  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enabled: isAliveEnabled,
  })

  const queryParams: TasksQueryParams = {
    archived: taskFilterToArchived(taskFilter),
    includeCounts,
    page: currentPage,
    pageSize,
  }

  // Include page and taskFilter in query key for proper cache separation
  const queryKey = getAgentTasksQueryKey(currentPage, taskFilter)
  const query = useCAPIQuery<FetchTasksResponse>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async () =>
      await fetchTasks({
        queryClient,
        queryParams,
        queryKey,
      }),
    initialData: initialTasks ? [initialTasks, false, undefined] : undefined,
    refetchInterval: isAliveEnabled ? false : sessionsPollingInterval,
    refetchIntervalInBackground: false,
  })

  // Refetch when pageSize changes since it's not in the query key
  // This ensures we fetch enough items when the user increases the page size
  const prevPageSizeRef = useRef(pageSize)
  useEffect(() => {
    if (prevPageSizeRef.current !== pageSize) {
      prevPageSizeRef.current = pageSize
      query.refetch()
    }
  }, [pageSize, query])

  // Get all tasks from the query result (already includes optimistic tasks)
  const allTasks = useMemo(() => query.data?.[0] || [], [query.data])
  const hasNextPage = query.data?.[1] ?? false
  const counts = query.data?.[2]

  // Get pull requests for tasks that have valid, non-optimistic resource IDs
  const tasksWithResourceIds = allTasks.filter(task => task.resource_id !== undefined && task.resource_id !== -1)
  const {
    data: idToPullRequestMap,
    isError: isErrorPullRequests,
    isFetching: isFetchingPullRequests,
    isLoading: isLoadingPullRequests,
  } = usePullRequests({
    tasks: tasksWithResourceIds,
    sessionsPollingInterval,
    initialPullRequestsMap,
  })

  // Get repo IDs for tasks without pull requests
  const repoIdsNeedingFetch = useMemo(() => {
    // Use initialPullRequestsMap when available (hard nav) for parallel loading with PR fetching
    const prMap = initialPullRequestsMap ?? idToPullRequestMap
    const ids: number[] = []
    for (const task of allTasks) {
      if (task.isOptimistic) continue
      if (task.resource_id && prMap[task.resource_id]) continue // Has PR, don't need to fetch repo info
      if (task.repo_id) {
        ids.push(task.repo_id)
      }
    }
    return ids
  }, [allTasks, initialPullRequestsMap, idToPullRequestMap])

  // Fetch repo info for tasks without pull requests
  const {data: repoMap, isLoading: isLoadingRepoInfo, isError: isErrorRepoInfo} = useRepoInfo(repoIdsNeedingFetch)

  // Attach pull request data to tasks where available, but only return hydrated or optimistic tasks
  // Memoize to prevent unnecessary re-renders of task list items when data hasn't changed
  const returnTasks = useMemo(() => {
    return allTasks
      .map(task => {
        const pull = task.resource_id ? idToPullRequestMap[task.resource_id] : undefined

        if (pull) {
          return {
            ...task,
            isOptimistic: false, // Be sure to always mark any transitional tasks that have pull requests as no longer optimistic
            pull,
            nwo: pull.repository_nwo,
          }
        } else {
          // If no pull request is found, return the task without pull data but include repo nwo
          const repo = task.repo_id ? repoMap[task.repo_id] : undefined
          return {
            ...task,
            pull: undefined,
            nwo: repo ? `${repo.ownerLogin}/${repo.name}` : task.nwo,
          }
        }
      })
      .filter(task => {
        // Always include optimistic tasks
        if (task.isOptimistic === true) {
          return true
        }

        // Include tasks with nwo (tasks without PRs yet) or tasks with PR data
        return task.pull !== undefined || task.nwo !== undefined
      }) as UITask[]
  }, [allTasks, idToPullRequestMap, repoMap])

  // Show loading when tasks, pull requests, or repo info are loading (for initial load)
  const isLoading =
    (query.isLoading && !query.data) ||
    (isLoadingPullRequests && !initialPullRequestsMap && allTasks.length > 0) ||
    (isLoadingRepoInfo && repoIdsNeedingFetch.length > 0 && allTasks.length > 0)

  const isError = query.isError || isErrorPullRequests || isErrorRepoInfo

  // Show fetching state when fetching tasks or pull requests
  const isFetching = query.isFetching || (isFetchingPullRequests && allTasks.length > 0)

  return {
    tasks: returnTasks,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    counts,
  }
}
